// Integration API Client
// Calls our backend endpoints for provider and connection management

import { api } from '../api-client';
import type {
  ApiResponse,
  ProviderResponse,
  UserConnection,
  AuthUrlResponse,
  ConnectRequest,
  GatewayResponse,
  ProviderEndpointGroup,
} from './types';

const BASE_PATH = '/api/v1/integrations';

// ============================================
// Provider APIs
// ============================================

/**
 * List all available providers
 */
export async function getProviders(search?: string): Promise<ProviderResponse[]> {
  const params = search ? { search } : undefined;
  const response = await api.get<ApiResponse<ProviderResponse[]>>(
    `${BASE_PATH}/providers`,
    { params }
  );
  return response.data || [];
}

/**
 * Get provider details including auth mode and credential fields
 */
export async function getProvider(provider: string): Promise<ProviderResponse> {
  const response = await api.get<ApiResponse<ProviderResponse>>(
    `${BASE_PATH}/providers/${provider}`
  );
  if (!response.data) {
    throw new Error(response.error || 'Provider not found');
  }
  return response.data;
}

/**
 * Get all unique categories from providers
 */
export async function getCategories(): Promise<string[]> {
  const response = await api.get<ApiResponse<string[]>>(
    `${BASE_PATH}/providers/categories`
  );
  return response.data || [];
}

/**
 * Get available API endpoints for a provider
 * Returns endpoint metadata including paths, methods, and descriptions
 */
export async function getProviderEndpoints(
  provider: string
): Promise<ProviderEndpointGroup | null> {
  try {
    const response = await api.get<ApiResponse<ProviderEndpointGroup>>(
      `${BASE_PATH}/providers/${provider}/endpoints`
    );
    return response.data || null;
  } catch {
    // Provider may not have endpoint metadata defined
    return null;
  }
}

// ============================================
// OAuth APIs
// ============================================

/**
 * Get OAuth authorization URL for a provider
 * Opens popup to this URL for OAuth flow
 */
export async function getAuthUrl(
  provider: string,
  scopes?: string[],
  connectionConfig?: Record<string, string>,
  displayName?: string,
  baseUrlOverride?: string
): Promise<AuthUrlResponse> {
  const params: Record<string, string> = {};
  if (scopes?.length) {
    params.scopes = scopes.join(',');
  }
  if (displayName) {
    params.displayName = displayName;
  }
  if (baseUrlOverride) {
    params.baseUrl = baseUrlOverride;
  }
  // Add connection_config fields as query params
  if (connectionConfig) {
    Object.entries(connectionConfig).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
  }
  const response = await api.get<ApiResponse<AuthUrlResponse>>(
    `${BASE_PATH}/auth-url/${provider}`,
    { params: Object.keys(params).length > 0 ? params : undefined }
  );
  if (!response.data) {
    throw new Error(response.error || 'Failed to get auth URL');
  }
  return response.data;
}

// ============================================
// Connection APIs
// ============================================

/**
 * Create connection for non-OAuth providers (API_KEY, BASIC, etc.)
 */
export async function createConnection(
  request: ConnectRequest
): Promise<UserConnection> {
  const response = await api.post<ApiResponse<UserConnection>>(
    `${BASE_PATH}/connect`,
    request
  );
  if (!response.data) {
    throw new Error(response.error || 'Failed to create connection');
  }
  return response.data;
}

/**
 * Finalize OAuth connection (called after OAuth callback)
 */
export async function finalizeOAuthConnection(
  provider: string,
  alias: string
): Promise<UserConnection> {
  // This is called from the callback page to register the connection
  // The actual OAuth token exchange happens on the backend callback
  const response = await api.post<ApiResponse<UserConnection>>(
    `${BASE_PATH}/callback`,
    { provider, connectionId: alias }
  );
  if (!response.data) {
    throw new Error(response.error || 'Failed to finalize connection');
  }
  return response.data;
}

/**
 * List current user's connections
 */
export async function getUserConnections(options?: {
  status?: string;
  provider?: string;
}): Promise<UserConnection[]> {
  const response = await api.get<ApiResponse<UserConnection[]>>(
    `${BASE_PATH}/user-connections`,
    { params: options }
  );
  return response.data || [];
}

/**
 * Delete a user connection
 */
export async function deleteConnection(alias: string): Promise<void> {
  await api.delete<ApiResponse<void>>(`${BASE_PATH}/user-connections/${alias}`);
}

/**
 * Set a connection as default for its provider
 */
export async function setDefaultConnection(alias: string): Promise<UserConnection> {
  const response = await api.put<ApiResponse<UserConnection>>(
    `${BASE_PATH}/user-connections/${alias}/default`
  );
  if (!response.data) {
    throw new Error(response.error || 'Failed to set default connection');
  }
  return response.data;
}

/**
 * Clear default status for a provider
 */
export async function clearDefaultConnection(provider: string): Promise<void> {
  await api.delete<ApiResponse<void>>(
    `${BASE_PATH}/user-connections/default/${provider}`
  );
}

// ============================================
// OAuth Popup Helper
// ============================================

export interface OAuthPopupOptions {
  url: string;
  width?: number;
  height?: number;
  onSuccess?: (params: URLSearchParams) => void;
  onError?: (error: string) => void;
  onClose?: () => void;
}

/**
 * Open OAuth popup window
 * Returns a cleanup function to close the popup
 */
export function openOAuthPopup(options: OAuthPopupOptions): () => void {
  const {
    url,
    width = 600,
    height = 700,
    onSuccess,
    onError,
    onClose,
  } = options;

  // Calculate center position
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  // Open popup
  const popup = window.open(
    url,
    'oauth-popup',
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
  );

  if (!popup) {
    onError?.('Popup was blocked by browser');
    return () => {};
  }

  // Poll for popup close or redirect
  const interval = setInterval(() => {
    try {
      // Check if popup was closed
      if (popup.closed) {
        clearInterval(interval);
        onClose?.();
        return;
      }

      // Check if redirected to our callback URL
      const popupUrl = popup.location.href;
      if (popupUrl.includes('/integrations/callback')) {
        clearInterval(interval);
        const params = new URLSearchParams(popup.location.search);
        popup.close();

        if (params.get('error')) {
          onError?.(params.get('error') || 'OAuth failed');
        } else if (params.get('success')) {
          onSuccess?.(params);
        }
      }
    } catch {
      // Cross-origin error - popup is still on provider's domain
    }
  }, 500);

  // Return cleanup function
  return () => {
    clearInterval(interval);
    if (popup && !popup.closed) {
      popup.close();
    }
  };
}

// ============================================
// Gateway API (Data Browser)
// ============================================

const GATEWAY_PATH = '/api/v1/gateway';

/**
 * Fetch data from gateway proxy
 * @param alias - Connection alias (e.g., 'sf_abc123')
 * @param endpoint - Resource path (e.g., '/contacts')
 * @param query - Query parameters to pass to the provider
 */
export async function fetchGatewayData<T = unknown>(
  alias: string,
  endpoint: string,
  query?: Record<string, string>
): Promise<GatewayResponse<T>> {
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const path = `${GATEWAY_PATH}/${alias}${normalizedEndpoint}`;
  return api.get<GatewayResponse<T>>(path, { params: query });
}

/**
 * Post data to gateway proxy (for complex queries)
 * @param alias - Connection alias
 * @param endpoint - Resource path
 * @param body - Request body
 * @param query - Query parameters
 */
export async function postGatewayData<T = unknown>(
  alias: string,
  endpoint: string,
  body?: unknown,
  query?: Record<string, string>
): Promise<GatewayResponse<T>> {
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const path = `${GATEWAY_PATH}/${alias}${normalizedEndpoint}`;
  return api.post<GatewayResponse<T>>(path, body, { params: query });
}

export default {
  getProviders,
  getProvider,
  getCategories,
  getProviderEndpoints,
  getAuthUrl,
  createConnection,
  finalizeOAuthConnection,
  getUserConnections,
  deleteConnection,
  setDefaultConnection,
  clearDefaultConnection,
  openOAuthPopup,
  fetchGatewayData,
  postGatewayData,
};
